import type { PlanResult } from "@decopl/deco-engine";
import { consumptionForSegments, overallCeilingDepth } from "@decopl/deco-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { STR } from "../i18n.js";
import { useStore } from "../store.js";

interface Props {
  result: PlanResult | null;
}

interface HoverInfo {
  time: number;
  depth: number;
  ceiling: number;
  tankP: number;
  x: number;
  y: number;
}

export function DiveProfileChart({ result }: Props) {
  const { gfHigh, sac, tankVolume, tankStartPressure, lang, showDepth, showCeiling, showTankPressure, toggle } = useStore();
  const t = STR[lang];
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 360 });
  const [hover, setHover] = useState<HoverInfo | null>(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver((entries) => {
      for (const ent of entries) {
        const { width, height } = ent.contentRect;
        if (width > 0 && height > 0) setSize({ w: width, h: height });
      }
    });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const data = useMemo(() => {
    if (!result) return null;
    const segs = result.segments;
    const gf = gfHigh / 100;
    const consumption = consumptionForSegments(segs, {
      sac,
      tank: { volume: tankVolume, startPressure: tankStartPressure },
    });
    const points: { t: number; depth: number; ceiling: number; tankP: number; kind: string }[] = [
      { t: 0, depth: 0, ceiling: 0, tankP: tankStartPressure, kind: "start" },
    ];
    let maxDepth = 0;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]!;
      const ceil = overallCeilingDepth(seg.tissuesAfter, gf);
      const c = consumption[i]!;
      points.push({
        t: seg.endTime,
        depth: seg.endDepth,
        ceiling: ceil,
        tankP: Math.max(0, c.tankPressureAfter),
        kind: seg.kind,
      });
      if (seg.endDepth > maxDepth) maxDepth = seg.endDepth;
      if (seg.startDepth > maxDepth) maxDepth = seg.startDepth;
    }
    return {
      points,
      maxTime: points[points.length - 1]!.t,
      maxDepth: Math.max(maxDepth, 10) * 1.1,
      decoStops: result.decoStops,
    };
  }, [result, gfHigh, sac, tankVolume, tankStartPressure]);

  const M = { l: 56, r: 56, t: 14, b: 28 };
  const W = size.w;
  const H = size.h;
  const pw = Math.max(20, W - M.l - M.r);
  const ph = Math.max(20, H - M.t - M.b);

  if (!data || !result) {
    return (
      <div className="profile-wrap">
        <div className="panel-head">
          <h3>{t.profile}</h3>
        </div>
        <div className="profile-chart" ref={wrapRef}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--ink-3)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
            }}
          >
            {lang === "fr" ? "En attente de calcul…" : "Awaiting calculation…"}
          </div>
        </div>
      </div>
    );
  }

  const xScale = (tt: number) => M.l + (tt / data.maxTime) * pw;
  const yScale = (d: number) => M.t + (d / data.maxDepth) * ph;
  const tankYScale = (p: number) => M.t + (1 - p / tankStartPressure) * ph;

  const depthPath = data.points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.t).toFixed(2)} ${yScale(p.depth).toFixed(2)}`)
    .join(" ");
  const fillPath =
    depthPath +
    ` L ${xScale(data.maxTime).toFixed(2)} ${(M.t + ph).toFixed(2)} L ${M.l.toFixed(2)} ${(M.t + ph).toFixed(2)} Z`;

  let ceilingPath = `M ${M.l} ${yScale(0)}`;
  for (let i = 1; i < data.points.length; i++) {
    const prev = data.points[i - 1]!;
    const cur = data.points[i]!;
    ceilingPath += ` L ${xScale(prev.t).toFixed(2)} ${yScale(cur.ceiling).toFixed(2)}`;
    ceilingPath += ` L ${xScale(cur.t).toFixed(2)} ${yScale(cur.ceiling).toFixed(2)}`;
  }

  const tankPath = data.points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xScale(p.t).toFixed(2)} ${tankYScale(p.tankP).toFixed(2)}`)
    .join(" ");

  const depthTicks: number[] = [];
  const depthStep = data.maxDepth > 60 ? 20 : data.maxDepth > 30 ? 10 : 5;
  for (let d = 0; d <= data.maxDepth; d += depthStep) depthTicks.push(d);

  const timeTicks: number[] = [];
  const timeStep =
    data.maxTime > 120 ? 30 : data.maxTime > 60 ? 15 : data.maxTime > 30 ? 5 : 2;
  for (let tt = 0; tt <= data.maxTime; tt += timeStep) timeTicks.push(tt);

  const handleMove = (ev: React.MouseEvent<HTMLDivElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const time = ((x - M.l) / pw) * data.maxTime;
    if (time < 0 || time > data.maxTime) {
      setHover(null);
      return;
    }
    let idx = data.points.findIndex((p) => p.t >= time);
    if (idx < 1) idx = 1;
    const a = data.points[idx - 1]!;
    const b = data.points[idx]!;
    const f = (time - a.t) / Math.max(0.001, b.t - a.t);
    setHover({
      time,
      depth: a.depth + (b.depth - a.depth) * f,
      ceiling: b.ceiling,
      tankP: a.tankP + (b.tankP - a.tankP) * f,
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
    });
  };

  const safetyTop = yScale(3);
  const safetyBot = yScale(6);

  return (
    <div className="profile-wrap">
      <div className="panel-head">
        <h3>{t.profile}</h3>
        <div className="legend">
          <div
            className={`legend-item${showDepth ? "" : " off"}`}
            onClick={() => toggle("showDepth")}
            style={{ color: "var(--accent)" }}
          >
            <span className="legend-swatch" style={{ background: "currentColor" }} />
            <span style={{ color: "var(--ink-2)" }}>{t.depth_curve}</span>
          </div>
          <div
            className={`legend-item${showCeiling ? "" : " off"}`}
            onClick={() => toggle("showCeiling")}
            style={{ color: "var(--danger)" }}
          >
            <span className="legend-swatch dashed" style={{ color: "currentColor" }} />
            <span style={{ color: "var(--ink-2)" }}>{t.ceiling}</span>
          </div>
          <div
            className={`legend-item${showTankPressure ? "" : " off"}`}
            onClick={() => toggle("showTankPressure")}
            style={{ color: "var(--caution)" }}
          >
            <span className="legend-swatch" style={{ background: "currentColor" }} />
            <span style={{ color: "var(--ink-2)" }}>{t.tank_pressure}</span>
          </div>
        </div>
      </div>
      <div
        className="profile-chart"
        ref={wrapRef}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <svg className="profile-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <defs>
            <linearGradient id="waterGrad" x1={0} y1={0} x2={0} y2={1}>
              <stop offset="0%" stopColor="var(--water-0)" stopOpacity={0.35} />
              <stop offset="50%" stopColor="var(--water-mid)" stopOpacity={0.45} />
              <stop offset="100%" stopColor="var(--water-deep)" stopOpacity={0.55} />
            </linearGradient>
            <linearGradient id="depthFill" x1={0} y1={0} x2={0} y2={1}>
              <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.18} />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
            </linearGradient>
          </defs>

          <rect x={M.l} y={M.t} width={pw} height={ph} fill="url(#waterGrad)" opacity={0.7} />

          <line
            x1={M.l}
            x2={W - M.r}
            y1={M.t}
            y2={M.t}
            stroke="var(--accent)"
            strokeWidth={1}
            opacity={0.5}
          />

          {/* Safety stop band 3-6 m */}
          {safetyBot > M.t && safetyTop < M.t + ph && (
            <g>
              <rect
                x={M.l}
                y={Math.max(M.t, safetyTop)}
                width={pw}
                height={Math.max(0, Math.min(M.t + ph, safetyBot) - Math.max(M.t, safetyTop))}
                fill="var(--safe)"
                opacity={0.07}
              />
              <line
                x1={M.l}
                x2={W - M.r}
                y1={yScale(3)}
                y2={yScale(3)}
                stroke="var(--safe)"
                strokeWidth={0.6}
                strokeDasharray="2 3"
                opacity={0.5}
              />
              <line
                x1={M.l}
                x2={W - M.r}
                y1={yScale(6)}
                y2={yScale(6)}
                stroke="var(--safe)"
                strokeWidth={0.6}
                strokeDasharray="2 3"
                opacity={0.5}
              />
              <text
                x={W - M.r - 6}
                y={(Math.max(M.t, safetyTop) + Math.min(M.t + ph, safetyBot)) / 2 + 3}
                textAnchor="end"
                fill="var(--safe)"
                fontSize={9}
                fontFamily="var(--font-mono)"
                letterSpacing="0.12em"
                opacity={0.75}
              >
                {lang === "fr" ? "SÉCU 3–6 m" : "SAFETY 3–6 m"}
              </text>
            </g>
          )}

          {/* Y-axis depth grid + ticks */}
          {depthTicks.map((d) => (
            <g key={`d${d}`}>
              <line
                x1={M.l}
                x2={W - M.r}
                y1={yScale(d)}
                y2={yScale(d)}
                stroke="var(--line)"
                strokeWidth={0.5}
                opacity={0.4}
              />
              <text
                x={M.l - 6}
                y={yScale(d) + 3}
                textAnchor="end"
                fill="var(--ink-3)"
                fontSize={10}
                fontFamily="var(--font-mono)"
              >
                {d.toFixed(0)}
              </text>
            </g>
          ))}

          {/* X-axis time ticks */}
          {timeTicks.map((tt) => (
            <g key={`t${tt}`}>
              <line
                x1={xScale(tt)}
                x2={xScale(tt)}
                y1={M.t}
                y2={M.t + ph}
                stroke="var(--line)"
                strokeWidth={0.5}
                opacity={0.3}
              />
              <text
                x={xScale(tt)}
                y={M.t + ph + 14}
                textAnchor="middle"
                fill="var(--ink-3)"
                fontSize={10}
                fontFamily="var(--font-mono)"
              >
                {tt.toFixed(0)}
              </text>
            </g>
          ))}

          {/* Right axis (tank pressure) ticks */}
          {showTankPressure &&
            [0, 50, 100, 150, 200, 250, 300]
              .filter((p) => p <= tankStartPressure)
              .map((p) => (
                <text
                  key={`tp${p}`}
                  x={W - M.r + 6}
                  y={tankYScale(p) + 3}
                  textAnchor="start"
                  fill="var(--caution)"
                  fontSize={10}
                  fontFamily="var(--font-mono)"
                  opacity={0.7}
                >
                  {p}
                </text>
              ))}

          {/* Y-axis label */}
          <text
            x={12}
            y={M.t + ph / 2}
            transform={`rotate(-90 12 ${M.t + ph / 2})`}
            textAnchor="middle"
            fill="var(--ink-3)"
            fontSize={9}
            fontFamily="var(--font-mono)"
            letterSpacing="0.14em"
          >
            {lang === "fr" ? "PROFONDEUR (m)" : "DEPTH (m)"}
          </text>

          {showTankPressure && (
            <text
              x={W - 8}
              y={M.t + ph / 2}
              transform={`rotate(90 ${W - 8} ${M.t + ph / 2})`}
              textAnchor="middle"
              fill="var(--caution)"
              fontSize={9}
              fontFamily="var(--font-mono)"
              letterSpacing="0.14em"
              opacity={0.7}
            >
              {lang === "fr" ? "PRESSION (bar)" : "PRESSURE (bar)"}
            </text>
          )}

          <text
            x={M.l + pw / 2}
            y={H - 4}
            textAnchor="middle"
            fill="var(--ink-3)"
            fontSize={9}
            fontFamily="var(--font-mono)"
            letterSpacing="0.14em"
          >
            {lang === "fr" ? "TEMPS (min)" : "TIME (min)"}
          </text>

          {/* Deco stop markers */}
          {data.decoStops.map((s, i) => {
            const seg = result.segments.find(
              (sg) => sg.kind === "stop" && sg.startDepth === s.depth,
            );
            if (!seg) return null;
            const x1 = xScale(seg.startTime);
            const x2 = xScale(seg.endTime);
            return (
              <g key={`ds${i}`}>
                <rect
                  x={x1}
                  y={yScale(s.depth) - 3}
                  width={x2 - x1}
                  height={6}
                  fill="var(--accent)"
                  opacity={0.3}
                  rx={1}
                />
                <text
                  x={(x1 + x2) / 2}
                  y={yScale(s.depth) - 6}
                  textAnchor="middle"
                  fill="var(--accent)"
                  fontSize={9}
                  fontFamily="var(--font-mono)"
                  fontWeight={600}
                >
                  {s.duration}'
                </text>
              </g>
            );
          })}

          {showDepth && <path d={fillPath} fill="url(#depthFill)" />}
          {showDepth && (
            <path
              d={depthPath}
              stroke="var(--accent)"
              strokeWidth={2}
              fill="none"
              strokeLinejoin="round"
            />
          )}
          {showCeiling && (
            <path
              d={ceilingPath}
              stroke="var(--danger)"
              strokeWidth={1.5}
              fill="none"
              strokeDasharray="5 4"
              opacity={0.85}
            />
          )}
          {showTankPressure && (
            <path
              d={tankPath}
              stroke="var(--caution)"
              strokeWidth={1.5}
              fill="none"
              opacity={0.8}
            />
          )}

          {hover && (
            <line
              x1={hover.x}
              x2={hover.x}
              y1={M.t}
              y2={M.t + ph}
              stroke="var(--ink-2)"
              strokeWidth={1}
              opacity={0.4}
              strokeDasharray="2 3"
            />
          )}
          {hover && showDepth && (
            <circle
              cx={hover.x}
              cy={yScale(hover.depth)}
              r={4}
              fill="var(--accent)"
              stroke="var(--bg-panel)"
              strokeWidth={2}
            />
          )}
        </svg>

        {hover && (
          <div
            className="chart-tooltip"
            style={{
              left: Math.min(hover.x + 12, W - 140),
              top: Math.min(hover.y + 12, H - 90),
            }}
          >
            <div className="tt-time">t = {hover.time.toFixed(1)} min</div>
            {showDepth && (
              <div className="tt-row">
                <span className="name" style={{ color: "var(--accent)" }}>
                  {t.depth_curve}
                </span>
                <span className="val">{hover.depth.toFixed(1)} m</span>
              </div>
            )}
            {showCeiling && (
              <div className="tt-row">
                <span className="name" style={{ color: "var(--danger)" }}>
                  {t.ceiling}
                </span>
                <span className="val">{hover.ceiling.toFixed(1)} m</span>
              </div>
            )}
            {showTankPressure && (
              <div className="tt-row">
                <span className="name" style={{ color: "var(--caution)" }}>
                  {lang === "fr" ? "Bouteille" : "Tank"}
                </span>
                <span className="val">{hover.tankP.toFixed(0)} bar</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
